import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /**
   * `compact` halves the cell padding for wide tables with many columns;
   * it reaches every cell so callers need not repeat it per cell.
   */
  density?: 'default' | 'compact';
}

const DENSITY_CLASS: Record<NonNullable<TableProps['density']>, string> = {
  default: '',
  compact: '[&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2',
};

/**
 * Data table after the reference design: a card-like wrapper that scrolls
 * horizontally, eyebrow-styled column headers, hairline row separators.
 */
const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, density = 'default', ...props }, ref) => (
    <div className="w-full overflow-x-auto rounded-card bg-paper-2 ring-1 ring-line">
      <table
        ref={ref}
        className={cn(
          'w-full min-w-full text-sm text-ink',
          DENSITY_CLASS[density],
          className
        )}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-b border-line', className)}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('divide-y divide-line', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn('transition-colors hover:bg-paper-3', className)}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn('dc-eyebrow px-4 py-3 text-left', className)}
    {...props}
  />
));
TableHeaderCell.displayName = 'TableHeaderCell';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('px-4 py-3 align-middle', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

export { Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell };
